import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Boxes, Plus, Search, MoreVertical, Trash2, Eye, RefreshCw, Plane, Edit } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

const COMPONENT_STATUS = {
  installed: { label: 'Installed', color: 'bg-green-500/20 text-green-400' },
  serviceable: { label: 'Serviceable', color: 'bg-blue-500/20 text-blue-400' },
  unserviceable: { label: 'Unserviceable', color: 'bg-red-500/20 text-red-400' },
  in_shop: { label: 'In Shop', color: 'bg-yellow-500/20 text-yellow-400' },
  retired: { label: 'Retired', color: 'bg-gray-500/20 text-gray-400' },
};

const COMPONENT_CATEGORIES = [
  { value: 'apu', label: 'APU' },
  { value: 'landing_gear', label: 'Landing Gear' },
  { value: 'avionics', label: 'Avionics' },
  { value: 'flight_controls', label: 'Flight Controls' },
  { value: 'hydraulics', label: 'Hydraulics' },
  { value: 'pneumatics', label: 'Pneumatics' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'fuel_system', label: 'Fuel System' },
  { value: 'environmental', label: 'Environmental (ECS)' },
  { value: 'propeller', label: 'Propeller' },
  { value: 'other', label: 'Other' },
];

export default function ComponentsPage() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const { isDark } = useTheme();
  const [components, setComponents] = useState([]);
  const [aircraft, setAircraft] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    part_number: '', serial_number: '', description: '', category: 'avionics',
    manufacturer: '', status: 'serviceable', installed_aircraft_id: '', installed_location: '', 
    tsn: '', csn: '', owner_id: '', operator_id: ''
  });

  useEffect(() => {
    fetchComponents();
    fetchStats();
    fetchAircraft();
    fetchOrganizations();
  }, [api]);

  const fetchComponents = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (filterCategory !== 'all') params.append('category', filterCategory);
      const response = await api.get(`/components?${params.toString()}`);
      setComponents(response.data);
    } catch (error) {
      toast.error('Failed to fetch components');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/components/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchAircraft = async () => {
    try {
      const response = await api.get('/aircraft?limit=200');
      setAircraft(response.data);
    } catch (error) {
      console.error('Failed to fetch aircraft:', error);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await api.get('/organizations?limit=200');
      setOrganizations(response.data);
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      part_number: '', serial_number: '', description: '', category: 'avionics',
      manufacturer: '', status: 'serviceable', installed_aircraft_id: '', installed_location: '', 
      tsn: '', csn: '', owner_id: '', operator_id: ''
    });
    setEditMode(false);
    setEditingId(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setShowCreateDialog(true);
  };

  const handleCreateComponent = async () => {
    try {
      if (!formData.part_number || !formData.serial_number || !formData.description) {
        toast.error('Please fill required fields');
        return;
      }
      const payload = {
        ...formData,
        tsn: formData.tsn ? parseFloat(formData.tsn) : null,
        csn: formData.csn ? parseInt(formData.csn) : null,
      };
      
      if (editMode && editingId) {
        await api.put(`/components/${editingId}`, payload);
        toast.success('Component updated');
      } else {
        await api.post('/components', payload);
        toast.success('Component registered');
      }
      
      setShowCreateDialog(false);
      resetForm();
      fetchComponents();
      fetchStats();
    } catch (error) {
      toast.error(`Failed to ${editMode ? 'update' : 'create'} component`);
    }
  };

  const handleEditComponent = async (comp) => {
    try {
      const response = await api.get(`/components/${comp.id}`);
      const data = response.data;
      
      setFormData({
        part_number: data.part_number || '',
        serial_number: data.serial_number || '',
        description: data.description || '',
        category: data.category || 'avionics',
        manufacturer: data.manufacturer || '',
        status: data.status || 'serviceable',
        installed_aircraft_id: data.installed_aircraft_id || '',
        installed_location: data.installed_location || '',
        tsn: data.tsn?.toString() || '',
        csn: data.csn?.toString() || '',
        owner_id: data.owner_id || '',
        operator_id: data.operator_id || ''
      });
      
      setEditMode(true);
      setEditingId(comp.id);
      setShowCreateDialog(true);
    } catch (error) {
      toast.error('Failed to load component details');
    }
  };

  const getOrgsByType = (type) => {
    return organizations.filter(org => org.organization_types?.includes(type));
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className={cn("text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>Components</h1>
          <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Major components tracking (APU, Landing Gear, Avionics)</p>
        </div>
        <Button onClick={openCreateDialog} className="gap-2" data-testid="create-component-btn">
          <Plus className="w-4 h-4" /> Add Component
        </Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
            <CardContent className="p-4">
              <div className={cn("text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>{stats.total || 0}</div>
              <div className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Total Components</div>
            </CardContent>
          </Card>
          <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-500">{stats.by_status?.installed || 0}</div>
              <div className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Installed</div>
            </CardContent>
          </Card>
          <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-blue-500">{stats.by_status?.serviceable || 0}</div>
              <div className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Serviceable</div>
            </CardContent>
          </Card>
          <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-yellow-500">{stats.by_status?.in_shop || 0}</div>
              <div className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>In Shop</div>
            </CardContent>
          </Card>
        </div>
      )}

      <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input placeholder="Search by P/N, S/N, description..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-[180px]"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {COMPONENT_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchComponents}><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className={isDark ? "border-slate-800" : ""}>
                <TableHead className={isDark ? "text-slate-400" : ""}>Part / Serial</TableHead>
                <TableHead className={isDark ? "text-slate-400" : ""}>Description</TableHead>
                <TableHead className={isDark ? "text-slate-400" : ""}>Category</TableHead>
                <TableHead className={isDark ? "text-slate-400" : ""}>Installed On</TableHead>
                <TableHead className={isDark ? "text-slate-400" : ""}>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8"><RefreshCw className="w-4 h-4 animate-spin inline" /> Loading...</TableCell></TableRow>
              ) : components.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8">No components found</TableCell></TableRow>
              ) : (
                components.map(cmp => (
                  <TableRow key={cmp.id} className={cn(isDark ? "border-slate-800 hover:bg-slate-800/50" : "hover:bg-gray-50")}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Boxes className={cn("w-4 h-4", isDark ? "text-purple-400" : "text-purple-600")} />
                        <div>
                          <div className={cn("font-mono text-xs", isDark ? "text-white" : "")}>{cmp.part_number}</div>
                          <div className={cn("font-mono text-xs", isDark ? "text-slate-400" : "text-gray-500")}>{cmp.serial_number}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className={cn("text-sm", isDark ? "text-white" : "")}>{cmp.description}</div>
                      {cmp.manufacturer && <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>{cmp.manufacturer}</div>}
                    </TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{cmp.category}</Badge></TableCell>
                    <TableCell>
                      {cmp.installed_aircraft_registration ? (
                        <div className="flex items-center gap-1 text-sm">
                          <Plane className="w-3 h-3" />
                          <span className="font-mono">{cmp.installed_aircraft_registration}</span>
                        </div>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge className={COMPONENT_STATUS[cmp.status]?.color}>{COMPONENT_STATUS[cmp.status]?.label || cmp.status}</Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem><Eye className="w-4 h-4 mr-2" />View Details</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEditComponent(cmp)}><Edit className="w-4 h-4 mr-2" />Edit Component</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-red-500"><Trash2 className="w-4 h-4 mr-2" />Retire</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showCreateDialog} onOpenChange={(open) => { if (!open) resetForm(); setShowCreateDialog(open); }}>
        <DialogContent className={cn("max-w-lg", isDark ? "bg-slate-900 border-slate-800" : "")}>
          <DialogHeader><DialogTitle className={isDark ? "text-white" : ""}>{editMode ? 'Edit Component' : 'Register Component'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={isDark ? "text-slate-300" : ""}>Part Number *</Label>
                <Input value={formData.part_number} onChange={(e) => setFormData(p => ({...p, part_number: e.target.value}))} placeholder="P/N" className="font-mono" />
              </div>
              <div className="space-y-2">
                <Label className={isDark ? "text-slate-300" : ""}>Serial Number *</Label>
                <Input value={formData.serial_number} onChange={(e) => setFormData(p => ({...p, serial_number: e.target.value}))} placeholder="S/N" className="font-mono" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className={isDark ? "text-slate-300" : ""}>Description *</Label>
              <Input value={formData.description} onChange={(e) => setFormData(p => ({...p, description: e.target.value}))} placeholder="Component description" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={isDark ? "text-slate-300" : ""}>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData(p => ({...p, category: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{COMPONENT_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className={isDark ? "text-slate-300" : ""}>Manufacturer</Label>
                <Input value={formData.manufacturer} onChange={(e) => setFormData(p => ({...p, manufacturer: e.target.value}))} placeholder="OEM name" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={isDark ? "text-slate-300" : ""}>Installed Aircraft</Label>
                <Select value={formData.installed_aircraft_id} onValueChange={(v) => setFormData(p => ({...p, installed_aircraft_id: v}))}>
                  <SelectTrigger><SelectValue placeholder="Select aircraft" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Not installed</SelectItem>
                    {aircraft.map(ac => <SelectItem key={ac.id} value={ac.id}>{ac.registration}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className={isDark ? "text-slate-300" : ""}>Location (ATA/Zone)</Label>
                <Input value={formData.installed_location} onChange={(e) => setFormData(p => ({...p, installed_location: e.target.value}))} placeholder="ATA 32" />
              </div>
            </div>
            {/* Owner & Operator */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={isDark ? "text-slate-300" : ""}>Owner</Label>
                <Select value={formData.owner_id} onValueChange={(v) => setFormData(p => ({...p, owner_id: v}))}>
                  <SelectTrigger><SelectValue placeholder="Select owner" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {getOrgsByType('owner').map(org => (
                      <SelectItem key={org.id} value={org.id}>{org.legal_name}</SelectItem>
                    ))}
                    {getOrgsByType('leasing_company').map(org => (
                      <SelectItem key={org.id} value={org.id}>{org.legal_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className={isDark ? "text-slate-300" : ""}>Operator</Label>
                <Select value={formData.operator_id} onValueChange={(v) => setFormData(p => ({...p, operator_id: v}))}>
                  <SelectTrigger><SelectValue placeholder="Select operator" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {getOrgsByType('operator').map(org => (
                      <SelectItem key={org.id} value={org.id}>{org.legal_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateComponent}>{editMode ? 'Update Component' : 'Register Component'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
